import { PortableText, type PortableTextComponents } from '@portabletext/react';
import type { ReactNode } from 'react';

import { NavLink } from './nav-link';
import { urlForImage } from '@todo/core/sanity/client';
import type { ArticleDetail } from '@todo/core/sanity/queries';
import { SITE } from '@site';

import css from './portable-text.module.css';

type Body = ArticleDetail['body'];

/**
 * The authored heading levels mapped onto an outline from <h2> (the page's
 * <h1> is the title): the shallowest style the document uses becomes h2 —
 * the rule riviera's renderer follows (root AGENTS.md, the `body` field).
 */
function headingLevels(blocks: Body): (authored: number) => number {
  const used = new Set<number>();
  for (const block of blocks ?? []) {
    const style = (block as { style?: string }).style;
    const match = typeof style === 'string' ? /^h([1-6])$/.exec(style) : null;
    if (block._type === 'block' && match) used.add(Number(match[1]));
  }
  const shallowest = used.size ? Math.min(...used) : 2;
  return (authored) => Math.min(6, Math.max(2, authored - shallowest + 2));
}

/**
 * A link to this site as a path ("/en/ski-dubai"), for client-side navigation;
 * null for any other site. Editors paste absolute URLs (https://dubai-todo.com/…)
 * or write relative ones.
 */
function internalPath(href: string): string | null {
  if (href.startsWith('/') && !href.startsWith('//')) return href;
  try {
    const url = new URL(href);
    const site = new URL(SITE.url);
    return url.host === site.host ? `${url.pathname}${url.search}${url.hash}` : null;
  } catch {
    return null;
  }
}

function Heading({ level, children }: { level: number; children: ReactNode }) {
  const Tag = `h${level}` as 'h2';
  return <Tag className={level === 2 ? css.h2 : css.h3}>{children}</Tag>;
}

// A guide's body: Sanity Portable Text as plain DOM, styled for reading.
export function PortableTextBody({ value }: { value: Body }) {
  if (!value?.length) return null;
  const level = headingLevels(value);
  const heading = (authored: number) =>
    function H({ children }: { children?: ReactNode }) {
      return <Heading level={level(authored)}>{children}</Heading>;
    };

  const components: PortableTextComponents = {
    block: {
      normal: ({ children }) => <p className={css.p}>{children}</p>,
      h1: heading(1),
      h2: heading(2),
      h3: heading(3),
      h4: heading(4),
      h5: heading(5),
      h6: heading(6),
      blockquote: ({ children }) => <blockquote className={css.quote}>{children}</blockquote>,
    },
    list: {
      bullet: ({ children }) => <ul className={css.list}>{children}</ul>,
      number: ({ children }) => <ol className={css.list}>{children}</ol>,
    },
    listItem: {
      bullet: ({ children }) => <li className={css.item}>{children}</li>,
      number: ({ children }) => <li className={css.item}>{children}</li>,
    },
    marks: {
      link: ({ children, value: mark }) => {
        const href = typeof mark?.href === 'string' ? mark.href.trim() : '';
        if (!href) return <>{children}</>;
        const path = internalPath(href);
        return path ? (
          <NavLink href={path} className={css.link}>
            {children}
          </NavLink>
        ) : (
          <a href={href} target="_blank" rel="noopener noreferrer" className={css.link}>
            {children}
          </a>
        );
      },
    },
    types: {
      image: ({ value: image }) => {
        if (!image?.asset) return null;
        const alt = typeof image.alt === 'string' ? image.alt : '';
        const caption = typeof image.caption === 'string' ? image.caption : '';
        return (
          <figure className={css.figure}>
            <img
              className={css.image}
              src={urlForImage(image).width(1200).fit('max').url()}
              alt={alt}
              loading="lazy"
              decoding="async"
            />
            {caption ? <figcaption className={css.caption}>{caption}</figcaption> : null}
          </figure>
        );
      },
    },
  };

  return (
    <div className={css.body}>
      <PortableText value={value} components={components} />
    </div>
  );
}
